import { createContext, useContext, type ReactNode } from 'react';
import { useFetch } from '@moko/ui';
import {
  getDashboardComponents,
  type IDashboardComponentWithSource,
} from '../../../services/dashboardService';

export interface DashboardComponentsContextValue {
  components: IDashboardComponentWithSource[];
  loading: boolean;
  failed: boolean;
  reload: () => void;
}

const DashboardComponentsContext =
  createContext<DashboardComponentsContextValue | null>(null);

// Single owner of the merged component catalog (/a, /i, /p). Fetches once via
// the shared useFetch hook and shares the result with every consumer (the
// grid, the customize drawer, the panels) so the three catalog calls never
// fire more than once per mount.
export const DashboardComponentsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { data, loading, error, refetch } = useFetch<
    IDashboardComponentWithSource[]
  >({
    queryKey: [],
    queryFn: (ac) => getDashboardComponents(ac),
  });

  return (
    <DashboardComponentsContext.Provider
      value={{
        components: data ?? [],
        // useFetch starts loading=false; treat "no data and no error yet" as
        // loading so the customize drawer shows its skeleton until first load.
        loading: loading || (data === null && error === null),
        failed: error !== null,
        reload: refetch,
      }}
    >
      {children}
    </DashboardComponentsContext.Provider>
  );
};

export const useDashboardComponents = (): DashboardComponentsContextValue => {
  const ctx = useContext(DashboardComponentsContext);
  if (!ctx) {
    throw new Error(
      'useDashboardComponents must be used within a DashboardComponentsProvider'
    );
  }
  return ctx;
};
